import {
    Component,
    h,
    ComponentInterface,
    Event,
    Element,
    Prop,
    EventEmitter,
    Host,
    Method,
    State,
} from "@stencil/core";
import { hasSlot } from "../../utils/utils";

// reference implementation for generated ids
// https://github.com/ionic-team/ionic-framework/blob/master/core/src/components/input/input.tsx
let id = 0;

/**
 * @slot (flag) - The flag
 * @slot (label) - The label
 * @slot (helper-text) - The helper text
 */

@Component({
    tag: "uic-input-field",
    styleUrl: "input-field.scss",
})
export class InputField implements ComponentInterface {
    private inputId = `uic-input-${++id}`;
    private labelId = `uic-input-label-${id}`;
    private helperTextId = `uic-input-helper-text-${id}`;
    private input: HTMLInputElement;

    @State() hasFocus = false;

    @Element() host: HTMLUicInputFieldElement;

    /**
     * The type of the input.
     */
    @Prop() type: "email" | "number" | "password" | "search" | "text" | "url" =
        "text";

    /**
     * The name of the control, which is submitted with the form data.
     */
    @Prop() name: string;

    /**
     * The value.
     */
    @Prop({ mutable: true }) value: string = "";

    /**
     * The label.
     */
    @Prop() label: string;

    /**
     * The placeholder text.
     */
    @Prop() placeholder: string;

    /**
     * Set to true to disable the input.
     */
    @Prop({ reflect: true }) disabled = false;

    /**
     * Set to true for a readonly input.
     */
    @Prop() readonly = false;

    /**
     * The input's minlength attribute.
     */
    @Prop() minlength: number;

    /**
     * The input's maxlength attribute.
     */
    @Prop() maxlength: number;

    /**
     * The input's min attribute.
     */
    @Prop() min: number;

    /**
     * The input's max attribute.
     */
    @Prop() max: number;

    /**
     * The input's step attribute.
     */
    @Prop() step: number;

    /**
     * The input's autocaptialize attribute.
     */
    @Prop() autocapitalize: string;

    /**
     * The input's autocorrect attribute.
     */
    @Prop() autocorrect: string;

    /**
     * The input's autocomplete attribute.
     */
    @Prop() autocomplete: string = "off";

    /**
     * The input's autofocus attribute.
     */
    @Prop() autofocus: boolean;

    /**
     * If `true`, the element will have its spelling and grammar checked.
     */
    @Prop() spellcheck: boolean = false;

    /**
     * The input's pattern attribute.
     */
    @Prop() pattern: string;

    /**
     * Controls if field is marked as requiring some input in case user has
     * not entered anything.
     */
    @Prop() required: boolean = false;

    /**
     * If set to `true`, the value is considered incorrect.
     * An invalid state will show the error flag.
     */
    @Prop({ mutable: true }) invalid: boolean = false;

    /**
     * Controls if fields is marked as having changed since last save. This is
     * about some visual indicator, only. The actual saving is beyond the scope
     * of this component.
     */
    @Prop() unsaved: boolean = false;

    /**
     * If set to `true`, the user can clear all input using a button.
     */
    @Prop() clearable = false;

    /**
     * The inputmode of the input.
     * A hint to the browser for which keyboard to display.
     */
    @Prop() inputmode:
        | "none"
        | "text"
        | "decimal"
        | "numeric"
        | "tel"
        | "search"
        | "email"
        | "url";

    /**
     * The unit of the value.
     * Do not use in combination with `clearable`.
     */
    @Prop() unit: string;

    /**
     * An indicator.
     */
    @Prop() flag: string;

    /**
     * Information with low emphasis.
     */
    @Prop() helperText: string;

    /**
     * error message to show instead of helper text if `invalid` is set
     */
    @Prop() invalidText: string;

    /**
     * Emitted when a keyboard input occurred.
     */
    @Event() uicInput: EventEmitter;

    /**
     * Emitted when the value has changed.
     */
    @Event() uicChange: EventEmitter;

    /**
     * Emitted when the input loses focus.
     */
    @Event() uicBlur: EventEmitter<FocusEvent>;

    /**
     * Emitted when the input has focus.
     */
    @Event() uicFocus: EventEmitter<FocusEvent>;

    /**
     * Emitted when the input is invalid.
     */
    @Event() uicInvalid: EventEmitter;

    /**
     * Sets focus on the native `input` in `uic-input-field`. Use this method instead of the global
     * `input.focus()`.
     */
    @Method()
    async setFocus() {
        this.input.focus();
    }

    /**
     * Removed from the native `input` in `uic-input-field`. Use this method instead of the global
     * `input.blur()`.
     */
    @Method()
    async removeFocus() {
        this.input.blur();
    }

    /**
     * Selects all text in the input.
     */
    @Method()
    async select() {
        this.input.select();
    }

    /**
     * Performs a validity check on the input and sets `invalid` accordingly.
     */
    @Method()
    async reportValidity() {
        this.invalid = !this.input.checkValidity();
    }

    /**
     * Returns the native `<input>` element used under the hood.
     */
    @Method()
    getInputElement(): Promise<HTMLInputElement> {
        return Promise.resolve(this.input);
    }

    private handleClickTouchArea() {
        this.input.focus();
    }

    private handleInput() {
        this.value = this.input.value;
        this.uicInput.emit();
    }

    private handleChange() {
        this.value = this.input.value;
        this.uicChange.emit();
    }

    private handleFocus() {
        this.hasFocus = true;
        this.uicFocus.emit();
    }

    private handleBlur(event: FocusEvent) {
        // only emit blur if the whole component loses focus
        if (!this.host.contains(event.relatedTarget as HTMLElement)) {
            this.hasFocus = false;
            this.uicBlur.emit();
        }
    }

    private handleInvalid() {
        this.invalid = true;
        this.uicInvalid.emit();
    }

    private handleClear(event: Event) {
        event.stopPropagation();
        event.preventDefault();

        this.setFocus();
        this.value = "";
        this.uicChange.emit();
    }

    private getFlag(hasText: boolean): string {
        if (this.invalid) {
            return "uic:flag-error";
        }

        if (this.required && !hasText) {
            return "uic:flag-mandatory";
        }

        if (this.unsaved) {
            return "uic:flag-unsaved";
        }

        return this.flag;
    }

    render() {
        const hasText = this.value && this.value.length > 0;
        const clearable = this.clearable && hasText;
        const flag = this.getFlag(hasText);
        const helperText =
            this.invalid && this.invalidText != null
                ? this.invalidText
                : this.helperText;

        const hasLabel = !!this.label || hasSlot(this.host, "label");
        const hasHelperText = !!helperText || hasSlot(this.host, "helper-text");

        return (
            <Host
                class={{
                    "uic-input-field": true,
                    "uic-input-field--flag": !!flag,
                    "uic-input-field--unit": !!this.unit,
                    "uic-input-field--invalid": this.invalid,
                    "uic-input-field--focused": this.hasFocus,
                    "uic-input-field--has-label": hasLabel,
                    "uic-input-field--has-helper-text": hasHelperText,
                }}
                onClick={() => this.handleClickTouchArea()}
            >
                <label class="uic-input-field__label" htmlFor={this.inputId}>
                    <slot name="label">{this.label}</slot>
                </label>

                <div class="uic-input-field__input-wrapper">
                    <input
                        class="uic-input-field__input"
                        ref={(el) => (this.input = el)}
                        id={this.inputId}
                        type={this.type}
                        name={this.name}
                        placeholder={this.placeholder}
                        disabled={this.disabled}
                        readonly={this.readonly}
                        minLength={this.minlength}
                        maxLength={this.maxlength}
                        min={this.min}
                        max={this.max}
                        step={this.step}
                        value={this.value}
                        autoCapitalize={this.autocapitalize}
                        autoComplete={this.autocomplete}
                        autoCorrect={this.autocorrect}
                        autoFocus={this.autofocus}
                        spellcheck={this.spellcheck}
                        pattern={this.pattern}
                        required={this.required}
                        inputMode={this.inputmode}
                        aria-labelledby={this.labelId}
                        aria-describedby={this.helperTextId}
                        title={this.value}
                        onInput={() => this.handleInput()}
                        onChange={() => this.handleChange()}
                        onFocus={() => this.handleFocus()}
                        onBlur={(e) => this.handleBlur(e)}
                        onInvalid={() => this.handleInvalid()}
                    />

                    {clearable && hasText && (
                        <button
                            tabIndex={-1}
                            class="uic-input-field__clear-button"
                            onClick={(e) => this.handleClear(e)}
                        >
                            <uic-icon name="uic:cross"></uic-icon>
                        </button>
                    )}

                    <span class="uic-input-field__flag">
                        <slot name="flag">
                            <uic-icon name={flag}></uic-icon>
                        </slot>
                    </span>

                    {this.unit && (
                        <div class="uic-input-field__unit">{this.unit}</div>
                    )}
                </div>

                <div
                    class="uic-input-field__helper-text"
                    id={this.helperTextId}
                >
                    <slot name="helper-text">
                        {/**  wrapping in div because otherise, Stencil will fail to place the text inside the slot */}
                        <div class="uic-input-field__helper-text-inner">
                            {helperText}
                        </div>
                    </slot>
                </div>
            </Host>
        );
    }
}
